import { fetchPackages } from 'Api/AccountApi';
import { getDppSkus, getDppSkusByUserType, getSingleDppSku } from 'Api/DppApi';
import { isDefaultPlanFlow, isOctPriceTest, selectDefaultPlan } from 'Utils/Analytics/Amplitude';
import {
  getFreeSheerIdPlanFromType,
  isMilitaryUpgradePlan,
  isSheerIdFreePlan,
  isFirstResponderChurned,
  isFirstResponderUpgradePlan,
  isMilitaryChurned,
  isSheerIdPlan,
  isEligibleSheerIdUpgradePlan,
} from 'Utils/SheerIdHelpers';
import {
  DISABLE_SHIPPING_ADDRESS_PROMOS,
  FOUNDERS_CUTOFF_DATE,
  FOUNDERS_SERVICE_ID,
  FIRST_MONTH_FREE_PROMO,
  FIRST_MONTH_99C_PROMO,
  MONTHLY_ROKT_SERVICE_ID,
  FREE_WEEK_SKU,
  YEAR_UPGRADE_SKU,
  MONTH_UPGRADE_SKU,
  BF_HOLIDAY_RENEWAL_SKU,
  SILVER_PATRIOT_2_YEAR_SKU,
  HOLIDAY_2022_PACKAGE_ID,
  BLACK_FRIDAY_PACKAGE_ID,
} from './Constants';
import { getUserType } from './Dpp';

export const isFounderServiceId = (serviceId) => serviceId === FOUNDERS_SERVICE_ID;
export const isFoundingMember = (serviceId, startDate) =>
  isFounderServiceId(serviceId) && startDate < FOUNDERS_CUTOFF_DATE;

// utils
export function getFrequencyLabel({ duration, period }) {
  if (!duration || !period) return '';

  if (period === 'NoOfYears') {
    return duration > 1 ? `${duration} yrs` : `yr`;
  }
  if (period === 'NoOfMonths') {
    return duration > 1 ? `${duration} mos` : `mo`;
  }
  return '';
}

export const getPlanSelectFrequencyLabel = (plan) => {
  let per = '/';
  let time = plan.period === 'NoOfYears' ? 'yr' : 'mo';
  let plural = plan.duration > 1 ? 's' : '';

  let result = `/${plan.duration > 1 ? plan.duration : ''}${time}${plural}`;

  if (plan.hasPrePayPromotion) {
    per = ' for ';
    result = `${per}${plan.duration}${time}${plural}`;
  }
  return result;
};

export const getPlanDisplayPrice = (planPrice) => {
  if (!planPrice) return '';

  const price = planPrice.toFixed(2);
  const [dollars, cents] = price.split('.');

  return `$${dollars}${Number(cents) > 0 ? `.${cents}` : ''}`;
};

//Not to be used in Billing History or Invoice screens
export const getSimplifiedPlanDisplayName = (plan) => {
  /* TODO: put the FREE WEEK SKU planName in DPP
  And use this func for name only as fallback if no DPP name */
  let planName = 'Monthly';
  if (plan.planId === FREE_WEEK_SKU) {
    planName = 'Free';
  } else if (plan.planId === BF_HOLIDAY_RENEWAL_SKU) {
    planName = 'Yearly Special';
  } else if (plan.period === 'NoOfYears') {
    if (plan.duration > 1) {
      planName = '2 Year';
    } else {
      planName = 'Yearly';
    }
  }
  return planName;
};

export const getActivePlanSimplifiedName = (plans = [], activePlanId = '') => {
  const activePlan = plans.filter((plan) => plan.planId === activePlanId)[0];
  if (activePlan) {
    return getSimplifiedPlanDisplayName(activePlan);
  }
  return null;
};

export const getPlanButtonText = (hasSubscription, plan) => {
  let linkText = 'Select Plan';

  if (is7DayFreeTrial(plan)) {
    linkText = 'Start Your 7-Day Free Trial';
  } else if (is30DayFreeTrial(plan)) {
    linkText = 'Start Your 30-Day Free Trial';
  } else if (isFreeTrial(plan)) {
    linkText = 'Start Your Free Trial';
  } else if (plan?.dppContent?.ctaText) {
    linkText = plan.dppContent.ctaText;
  }
  if (hasSubscription) {
    linkText = plan?.dppContent?.ctaTextExistingUser || 'Update Plan';
  }

  return linkText;
};

const prePayPromotion = ({ promotionCategory = '' }) => promotionCategory === 'PrePay';

const freePromotions = ({ isFreeTrail }) => isFreeTrail;

/* The isFreeTrail field from Evergent is unreliable - e.g. the 1STMONTH99C (1st month for 99cents)
   has isFreeTrail = true. This function helps determine if it is actually a free trial
   for FE display purposes  */
export const isFreeTrial = (plan) => {
  return plan?.hasFreePromo && plan?.freePromo && !(plan?.freePromo.amount > 0);
};

export const is7DayFreeTrial = (plan) => {
  return plan?.hasFreePromo && plan?.freePromo?.period === 'NoOfDays' && plan?.freePromo?.duration === 8;
};

export const is30DayFreeTrial = (plan) => {
  return plan?.hasFreePromo && plan?.freePromo?.period === 'NoOfDays' && plan?.freePromo?.duration === 30;
};

const promotionalItem = ({ PromotionalItem = [] }) => {
  const hasPromotionalItems = Array.isArray(PromotionalItem) && PromotionalItem.length;
  if (hasPromotionalItems) {
    // temporary disabling shipping form. (roku promo have digital merch, so it does not require a shipping address)
    // Evergent is going to provide a flag at promo level to reflect non-digital merchandise
    const validPromotionalItems = PromotionalItem.some(
      (promo) => !DISABLE_SHIPPING_ADDRESS_PROMOS.includes(promo.serviceId)
    );
    return validPromotionalItems;
  }
  return false;
};

export const mapPlan = ({ promoMsg, pkgMsg = {} }) => {
  const promos = promoMsg || [];
  const hasPromotionalItems = promos.some(promotionalItem);
  const hasPrePayPromotion = promos.some(prePayPromotion);
  const hasFreePromo = promos.some(freePromotions);
  const prePayPromo = promos.find(prePayPromotion); // amount,duration,period,promotionID
  const freePromo = promos.find(freePromotions); // displayName,nextBillingDateTime,promotionID
  const hasMerchandise = hasPromotionalItems && hasPrePayPromotion;

  const { appId, packageID, tier, displayName, packageName, bbDescription, retailPrice, duration, period } = pkgMsg;

  const planName = displayName;
  const planPrice = hasPrePayPromotion ? prePayPromo.amount : retailPrice;
  const planFrequencyText = hasPrePayPromotion
    ? getFrequencyLabel(prePayPromo)
    : getFrequencyLabel({ duration, period });
  let promotionID = hasPrePayPromotion ? prePayPromo.promotionID : '';
  let frequency = hasPrePayPromotion ? prePayPromo : { duration, period };
  if (isSheerIdFreePlan(packageID) && freePromo) {
    promotionID = freePromo.promotionID || '';
    frequency = freePromo;
  }

  return {
    appId,
    promotionID,
    packageID,
    planId: packageID,
    tier,
    planName,
    packageName,
    duration: frequency.duration,
    period: frequency.period,
    planDescription: bbDescription,
    planPrice,
    hasMerchandise,
    planFrequencyText,
    freePromo,
    hasFreePromo,
    hasPrePayPromotion,
  };
};

export const mapPackages = (packages = []) => packages.map(mapPlan);

export const addAmplitudeExperiments = async (dppPlans) => {
  try {
    if (isOctPriceTest()) {
      //check if has both skus want to replace and save their position
      const results = {};
      dppPlans.forEach(({ planId }, idx) => {
        if (planId === 'FOXNATION1M-WEB2') {
          results['FOXNATION1M-WEB3'] = idx;
        } else if (planId === 'FOXNATION12M-WEB') {
          results['FOXNATION12M-WEB2'] = idx;
        }
      });

      //replace the regular plans with the new experiment plans
      if (Object.keys(results).length === 2) {
        const [newMonthly, newYearly] = await getDppSkus(['FOXNATION1M-WEB3', 'FOXNATION12M-WEB2']);
        dppPlans[results['FOXNATION1M-WEB3']] = newMonthly;
        dppPlans[results['FOXNATION12M-WEB2']] = newYearly;
      }
    }
  } finally {
    console.log('dppPlans: ', dppPlans);
    return dppPlans;
  }
};

export const isCurrentOrNextPlan = (subscription, planId) => {
  const nextSubId = subscription?.nextSubscription?.serviceID;
  return subscription.activeSubscriptionId === planId || nextSubId === planId;
};

export const missingCurrentNextPlanIds = (subscription, planIds) => {
  let missingIds = [];

  const nextSubId = subscription?.nextSubscription?.serviceID;

  if (!planIds.includes(subscription.activeSubscriptionId)) {
    missingIds = [subscription.activeSubscriptionId];
  }
  if (nextSubId && !planIds.includes(nextSubId)) {
    missingIds = [...missingIds, nextSubId];
  }

  return missingIds;
};

export const getPlanIdsFromSkus = (plans) => {
  return plans.map(({ planId }) => planId);
};

export const handleSheerIdPlans = async (subscription) => {
  const allSheerIdPlans = await fetchPackages();
  const backendPlans = getAvailableSheerIdPlans(subscription, allSheerIdPlans);

  const planIds = getPlanIdsFromSkus(backendPlans);
  const dppPlans = await getDppSkus(planIds);

  return { backendPlans, dppPlans };
};

const removeSameTierPlans = (plans, dominantPlanIds = []) => {
  const dominantPlanTiers = {};

  plans.forEach(({ planId, tier }) => {
    if (dominantPlanIds.includes(planId)) {
      dominantPlanTiers[tier] = true;
    }
  });

  return plans.filter(({ planId, tier }) => {
    const isDominantPlan = dominantPlanIds.some((id) => id === planId);
    if (isDominantPlan) return true;
    if (dominantPlanTiers[tier]) return false;
    return true;
  });
};

export const handleRoktPlan = async () => {
  const backendPlans = await fetchPackages([MONTHLY_ROKT_SERVICE_ID]);

  const roktPlanStrings = await getSingleDppSku(MONTHLY_ROKT_SERVICE_ID);
  const dppPlans = [roktPlanStrings];

  return { backendPlans, dppPlans };
};

export const handleBFHoliday2022RenewalPlan = async (plans, alreadySubscribed = false) => {
  // return if no plans at all are found
  if (plans.backendPlans?.length == 0 && plans.dppPlans?.length == 0) {
    return plans;
  }

  const twoYearPlanBackendIdx = plans.backendPlans.findIndex((plan) => plan.packageID === SILVER_PATRIOT_2_YEAR_SKU);
  const twoYearPlanDppIdx = plans.dppPlans.findIndex((plan) => plan.planId === SILVER_PATRIOT_2_YEAR_SKU);
  // return if two-year plan isn't available
  if (twoYearPlanBackendIdx < 0 || twoYearPlanDppIdx < 0) {
    return plans;
  }

  // if user is already subscribed to the renewal plan, it's already included so no need to fetch it
  // instead, simply need to remove the two-year plan
  const newPlans = { ...plans };
  if (alreadySubscribed) {
    newPlans.dppPlans.splice(twoYearPlanDppIdx, 1);
    newPlans.backendPlans.splice(twoYearPlanBackendIdx, 1);
  } else {
    let renewalPlanBackend;
    try {
      const fetchPkgsResult = await fetchPackages([BF_HOLIDAY_RENEWAL_SKU]);
      if (fetchPkgsResult?.length == 0) return plans;
      renewalPlanBackend = fetchPkgsResult[0];
    } catch (e) {
      return plans;
    }
    const renewalPlanDpp = await getSingleDppSku(BF_HOLIDAY_RENEWAL_SKU);
    if (Object.keys(renewalPlanDpp).length == 0) return plans;

    newPlans.dppPlans[twoYearPlanDppIdx] = renewalPlanDpp;
    newPlans.backendPlans[twoYearPlanBackendIdx] = renewalPlanBackend;
  }
  return newPlans;
};

/*TODO: REMOVE this logic a few weeks after the May 2023 streaming promo
this plan is only offered for a few days and ends after a week (non-renewable)
logic no longer needed 
*/
export const handleFreeWeekPlan = async (freeSku) => {
  const planIds = [freeSku, YEAR_UPGRADE_SKU, MONTH_UPGRADE_SKU];
  const backendPlans = await fetchPackages(planIds);
  const dppPlans = await getDppSkus(planIds);

  return { backendPlans, dppPlans };
};

export const handleGeneralPlans = async (subscription, planToSelect) => {
  let backendPlans = [];
  const userType = getUserType(subscription.hasActiveSubscription, subscription.isChurnedUser);
  let dppPlans = await getDppSkusByUserType(userType);
  dppPlans = await addAmplitudeExperiments(dppPlans);

  let planIds = getPlanIdsFromSkus(dppPlans);

  //CURRENT SUBSCRIBERS
  if (subscription.hasSubscription) {
    let additionalIds = missingCurrentNextPlanIds(subscription, planIds);

    const additionalPlanStrings = await getDppSkus(additionalIds);

    dppPlans = [...dppPlans, ...additionalPlanStrings];

    planIds = getPlanIdsFromSkus(dppPlans);

    backendPlans = await fetchPackages(planIds);

    //Shouldn't show other plans of same tier of current plan
    backendPlans = removeSameTierPlans(backendPlans, [subscription.activeSubscriptionId]);
  } else {
    backendPlans = await fetchPackages(planIds);
  }

  return { backendPlans, dppPlans };
};

export const getAvailableSheerIdPlans = (subscription, plans) => {
  let plansToDisplay = plans;

  //EXISTING SUBSCRIBERS:
  //always show current plan and, if there is one, next plan
  if (subscription.hasActiveSubscription) {
    //if has an active first responder or military plan, only show eligible upgrade plans
    if (isSheerIdPlan(subscription.activeSubscriptionId)) {
      plansToDisplay = plansToDisplay.filter(
        (plan) => isEligibleSheerIdUpgradePlan(subscription, plan) || isCurrentOrNextPlan(subscription, plan.planId)
      );
      //if newly verified in sheerid, show the free sheerid plan:
    } else if (getFreeSheerIdPlanFromType()) {
      plansToDisplay = plansToDisplay.filter(
        ({ planId }) => planId === getFreeSheerIdPlanFromType() || isCurrentOrNextPlan(subscription, planId)
      );
    }

    //NEW AND CHURNED USERS:
  } else {
    //if had a first responder plan previously, only show eligible first responder upgrade plans
    if (isFirstResponderChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
      plansToDisplay = plansToDisplay.filter(({ planId }) => isFirstResponderUpgradePlan(planId));
      //if had a military plan previously, only show eligible military upgrade plans
    } else if (isMilitaryChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
      plansToDisplay = plansToDisplay.filter(({ planId }) => isMilitaryUpgradePlan(planId));
      //if newly verified in sheerid and hasn't got a free sheerid plan yet, show the free sheerid plan:
    } else if (getFreeSheerIdPlanFromType()) {
      plansToDisplay = plansToDisplay.filter(({ planId }) => planId === getFreeSheerIdPlanFromType());
    }
  }

  return plansToDisplay;
};

export const handlePlanToSelect = (planToSelect, plans, activeSubscriptionId) => {
  let { planId = '' } = getPlanFromList(planToSelect, plans, activeSubscriptionId);

  if (!planId && isDefaultPlanFlow()) {
    planId = selectDefaultPlan(plans, activeSubscriptionId);
  }

  return planId;
};

export const getPlanFromList = (query = '', plans = [], activeSubscriptionId = '') => {
  const matchedPlan = plans.find((plan) => {
    const { planName = '', packageID = '' } = plan;
    //Don't match if no query of if existing user already has the plan
    if (!query || activeSubscriptionId === packageID) return false;
    const preSelectedPlan = query.toLowerCase();
    return planName.toLowerCase().startsWith(preSelectedPlan) || preSelectedPlan === packageID.toLowerCase();
  });
  return matchedPlan || {};
};

export const isFreeMonthPromoPlanActive = (selectedPlan) => {
  const { freePromo } = selectedPlan || {};
  if (freePromo) {
    return freePromo.promotionID === FIRST_MONTH_FREE_PROMO;
  }
  return false;
};

/*
  Helper to reduce plans (from /packages) and DPP Plan copy/images
  Needs to maintain order of plans from BE /packages (BE sorts by tier). so order is determined by backendPlans
  If there is somehow a mismatch of SKUs in each list, will only includes SKUs that are in both lists.
*/
export const getPlansWithDppContent = ({ backendPlans, dppPlans }) => {
  const planStringsMap = {};
  dppPlans.forEach((planString) => {
    planStringsMap[planString.planId] = planString;
  });

  let result = [];

  backendPlans.forEach((plan) => {
    const dppContent = planStringsMap[plan.planId];
    const dppContentOverride = plan.dppContentOverride || {};
    if (dppContent) {
      result.push({ ...plan, dppContent: { ...dppContent, ...dppContentOverride } });
    }
  });

  return result;
};

export const isMonthlyPromoPlanActive = (selectedPlan) => {
  const { freePromo } = selectedPlan || {};
  if (freePromo) {
    return freePromo.promotionID === FIRST_MONTH_99C_PROMO;
  }
  return false;
};
